import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useTranslation } from 'react-i18next';
import { useWebSocket } from '../../hooks/useWebSocket';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { toast } from 'sonner';
import { Send, Paperclip, Image, Loader2, Search, ArrowLeft, X, Download, Truck, Check, CheckCheck, Smile, Pencil, Trash2, MessageSquare } from 'lucide-react';
import { Link } from 'react-router-dom';

// Available reactions
const REACTIONS = ["👍", "❤️", "😂", "😮", "😢", "🙏", "✅", "🚚"];

// Message delivery status indicator component (WhatsApp-style)
const MessageStatus = ({ message, isOwnMessage }) => {
  if (!isOwnMessage) return null;
  
  // Determine status: sent → delivered → read
  const isRead = message.is_read;
  const isDelivered = message.delivered_at;
  
  if (isRead) {
    // Double blue checkmarks - read
    return (
      <span className="inline-flex items-center ml-1" title="Read">
        <CheckCheck className="h-3.5 w-3.5 text-blue-300" />
      </span>
    );
  } else if (isDelivered) {
    // Double gray checkmarks - delivered but not read
    return (
      <span className="inline-flex items-center ml-1" title="Delivered">
        <CheckCheck className="h-3.5 w-3.5 text-blue-200/60" />
      </span>
    );
  } else {
    // Single checkmark - sent
    return (
      <span className="inline-flex items-center ml-1" title="Sent">
        <Check className="h-3.5 w-3.5 text-blue-200/60" />
      </span>
    );
  }
};

// Reaction picker component
const ReactionPicker = ({ onReact, existingReactions, currentUserId }) => {
  const [open, setOpen] = useState(false);
  
  const handleReact = (reaction) => {
    onReact(reaction);
    setOpen(false);
  };
  
  // Check if current user has reacted with a specific emoji
  const hasReacted = (reaction) => {
    return existingReactions?.[reaction]?.includes(currentUserId);
  };
  
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button 
          className="opacity-0 group-hover:opacity-100 transition-opacity p-1 hover:bg-slate-700 rounded"
          title="Add reaction"
        >
          <Smile className="h-4 w-4 text-slate-400 hover:text-white" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-2 bg-slate-800 border-slate-700" side="top" align="start">
        <div className="flex gap-1">
          {REACTIONS.map((reaction) => (
            <button
              key={reaction}
              onClick={() => handleReact(reaction)}
              className={`text-xl p-1.5 rounded hover:bg-slate-700 transition-colors ${
                hasReacted(reaction) ? 'bg-blue-600/30 ring-1 ring-blue-500' : ''
              }`}
              title={hasReacted(reaction) ? 'Remove reaction' : 'Add reaction'}
            >
              {reaction}
            </button>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
};

// Display reactions on a message
const MessageReactions = ({ reactions, currentUserId, onReact }) => {
  if (!reactions || Object.keys(reactions).length === 0) return null;
  
  return (
    <div className="flex flex-wrap gap-1 mt-1">
      {Object.entries(reactions).map(([emoji, users]) => {
        if (!users || users.length === 0) return null;
        const hasReacted = users.includes(currentUserId);
        return (
          <button
            key={emoji}
            onClick={() => onReact(emoji)}
            className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-xs transition-colors ${
              hasReacted 
                ? 'bg-blue-600/40 border border-blue-500/50 hover:bg-blue-600/60' 
                : 'bg-slate-700/60 border border-slate-600/50 hover:bg-slate-700'
            }`}
            title={`${users.length} reaction${users.length > 1 ? 's' : ''}`}
          >
            <span>{emoji}</span>
            <span className="text-slate-300">{users.length}</span>
          </button>
        );
      })}
    </div>
  );
};

export const Messaging = () => {
  const { api, user } = useAuth();
  const { t } = useTranslation();
  const [conversations, setConversations] = useState([]);
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [messageLoading, setMessageLoading] = useState(false);
  const [newMessage, setNewMessage] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [showNewChat, setShowNewChat] = useState(false);
  const [pendingAttachments, setPendingAttachments] = useState([]);
  const [uploadingFile, setUploadingFile] = useState(false);
  const [previewImage, setPreviewImage] = useState(null);
  const [driverTasks, setDriverTasks] = useState({}); // Map of driver_id -> current task summary
  const [showGroupMessage, setShowGroupMessage] = useState(false);
  const [groupMessage, setGroupMessage] = useState('');
  const [sendingGroupMessage, setSendingGroupMessage] = useState(false);
  const [drivers, setDrivers] = useState([]);
  const [otherUserTyping, setOtherUserTyping] = useState(false);
  const [typingUsers, setTypingUsers] = useState({}); // Map of user_id -> true (for showing typing in conv list)
  const [conversationSearchTerm, setConversationSearchTerm] = useState('');
  const [searchMatchingUserIds, setSearchMatchingUserIds] = useState(null); // Search for conversations
  const [editingMessageId, setEditingMessageId] = useState(null);
  const [editContent, setEditContent] = useState('');
  const typingTimeoutRef = useRef(null);
  const typingTimeoutsRef = useRef({}); // Map of user_id -> timeout for typing indicators
  const messagesEndRef = useRef(null);
  const fileInputRef = useRef(null);
  const selectedConversationRef = useRef(null);
  const fetchConversationsRef = useRef(null);

  const API_URL = process.env.REACT_APP_BACKEND_URL;
  
  // Keep refs updated
  useEffect(() => {
    selectedConversationRef.current = selectedConversation;
  }, [selectedConversation]);
  
  // Handle WebSocket messages for real-time updates
  const handleWebSocketMessage = useCallback((data) => {
    if (data.type === 'new_message') {
      const msg = data.message;
      const isCatchUp = data.is_catch_up;  // Flag for messages fetched on reconnect
      
      // Clear typing indicator when message received
      if (msg.sender_id === selectedConversationRef.current?.other_user?.user_id) {
        setOtherUserTyping(false);
      }
      
      // If this message is for the current conversation, add it
      if (selectedConversationRef.current?.other_user?.user_id === msg.sender_id ||
          selectedConversationRef.current?.other_user?.user_id === msg.receiver_id) {
        setMessages(prev => {
          // Avoid duplicates
          if (prev.some(m => m.id === msg.id)) return prev;
          return [...prev, msg];
        });
        
        // Send read receipt for incoming messages
        if (msg.sender_id === selectedConversationRef.current?.other_user?.user_id) {
          api.post('/messages/read-receipt', {
            other_user_id: selectedConversationRef.current.other_user.user_id,
            message_ids: [msg.id]
          }).catch(() => {});
        }
      }
      
      // Refresh conversations to update last message
      if (fetchConversationsRef.current) {
        fetchConversationsRef.current();
      }
      
      // Show notification if message is from someone else (and not a catch-up message)
      // Note: Sound/vibrate/browser notification is handled by useWebSocket hook
      // Here we only refresh the conversation list
      if (msg.sender_id !== user?.user_id && !isCatchUp) {
        // Refresh conversations to update badges
        if (fetchConversationsRef.current) {
          fetchConversationsRef.current();
        }
      }
    } else if (data.type === 'unread_messages') {
      // Handle bulk unread messages (from reconnect)
      console.log(`Received ${data.count} unread messages on reconnect`);
      // Refresh conversations to show updated state
      if (fetchConversationsRef.current) {
        fetchConversationsRef.current();
      }
    } else if (data.type === 'typing_indicator') {
      // Handle typing indicator
      const senderId = data.sender_id;
      
      // Update typing state for the currently open conversation
      if (senderId === selectedConversationRef.current?.other_user?.user_id) {
        setOtherUserTyping(data.is_typing);
        
        // Auto-clear typing indicator after 5 seconds
        if (data.is_typing) {
          if (typingTimeoutRef.current) {
            clearTimeout(typingTimeoutRef.current);
          }
          typingTimeoutRef.current = setTimeout(() => {
            setOtherUserTyping(false);
          }, 5000);
        }
      }
      
      // Also track globally for conversation list preview
      if (data.is_typing) {
        setTypingUsers(prev => ({ ...prev, [senderId]: true }));
        
        // Clear existing timeout for this user
        if (typingTimeoutsRef.current[senderId]) {
          clearTimeout(typingTimeoutsRef.current[senderId]);
        }
        
        // Auto-clear after 5 seconds
        typingTimeoutsRef.current[senderId] = setTimeout(() => {
          setTypingUsers(prev => {
            const updated = { ...prev };
            delete updated[senderId];
            return updated;
          });
        }, 5000);
      } else {
        // Remove from typing users
        setTypingUsers(prev => {
          const updated = { ...prev };
          delete updated[senderId];
          return updated;
        });
        if (typingTimeoutsRef.current[senderId]) {
          clearTimeout(typingTimeoutsRef.current[senderId]);
        }
      }
    } else if (data.type === 'messages_read') {
      // Update message read status in real-time
      console.log('Messages read by:', data.reader_name, 'count:', data.read_count);
      
      // Update the is_read status for messages sent by current user to this reader
      setMessages(prev => prev.map(msg => {
        // Only update messages sent by current user to the reader
        if (msg.sender_id === user?.user_id && msg.receiver_id === data.reader_id && !msg.is_read) {
          return { ...msg, is_read: true, read_at: new Date().toISOString() };
        }
        return msg;
      }));
    } else if (data.type === 'message_delivered') {
      // Update message delivery status in real-time (single → double checkmark)
      console.log('Message delivered:', data.message_id);
      
      setMessages(prev => prev.map(msg => {
        if (msg.id === data.message_id && !msg.delivered_at) {
          return { ...msg, delivered_at: data.delivered_at };
        }
        return msg;
      }));
    } else if (data.type === 'message_reaction') {
      // Update message reactions in real-time
      console.log('Reaction update:', data.action, data.reaction, 'on message', data.message_id);
      
      setMessages(prev => prev.map(msg => {
        if (msg.id === data.message_id) {
          return { ...msg, reactions: data.reactions };
        }
        return msg;
      }));
      
      // Show toast for new reactions on your messages (not your own reactions)
      if (data.action === 'added' && data.user_id !== user?.user_id) {
        toast.info(`${data.user_name} reacted ${data.reaction}`, {
          duration: 3000
        });
      }
    } else if (data.type === 'message_edited') {
      setMessages(prev => prev.map(msg => {
        if (msg.id === data.message_id) {
          return { ...msg, content: data.content, edited: true, edited_at: data.edited_at };
        }
        return msg;
      }));
    } else if (data.type === 'message_deleted') {
      setMessages(prev => prev.map(msg =>
        msg.id === data.message_id ? { ...msg, content: 'This message was deleted', deleted: true, attachments: [], file_url: null } : msg
      ));
    }
  }, [user?.user_id, api]);

  const { connected } = useWebSocket(handleWebSocketMessage);
  
  // Send typing indicator
  const sendTypingIndicator = useCallback((isTyping) => {
    if (selectedConversation?.other_user?.user_id) {
      api.post('/messages/typing', {
        receiver_id: selectedConversation.other_user.user_id,
        is_typing: isTyping
      }).catch(() => {});
    }
  }, [api, selectedConversation]);
  
  // Debounce ref for typing indicator
  const typingDebounceRef = useRef(null);
  const isTypingRef = useRef(false);
  
  // Handle input change with debounced typing indicator
  const handleMessageInputChange = (e) => {
    setNewMessage(e.target.value);
    
    const hasText = e.target.value.trim().length > 0;
    
    // Send typing=true immediately when user starts typing
    if (hasText && !isTypingRef.current) {
      isTypingRef.current = true;
      sendTypingIndicator(true);
    }
    
    // Clear existing debounce timer
    if (typingDebounceRef.current) {
      clearTimeout(typingDebounceRef.current);
    }
    
    // Set new debounce timer to stop typing indicator after 2 seconds of inactivity
    if (hasText) {
      typingDebounceRef.current = setTimeout(() => {
        isTypingRef.current = false;
        sendTypingIndicator(false);
      }, 2000);
    } else {
      // Immediately stop typing if input is empty
      isTypingRef.current = false;
      sendTypingIndicator(false);
    }
  };
  
  // Add or toggle reaction on a message
  const handleReaction = useCallback(async (messageId, reaction) => {
    try {
      const response = await api.post(`/messages/react/${messageId}`, { reaction });
      
      // Update local state immediately (optimistic update already done via WebSocket)
      // This ensures we have the latest state even if WebSocket is slow
      setMessages(prev => prev.map(msg => {
        if (msg.id === messageId) {
          return { ...msg, reactions: response.data.reactions };
        }
        return msg;
      }));
    } catch (error) {
      console.error('Failed to add reaction:', error);
      toast.error('Failed to add reaction');
    }
  }, [api]);
  
  // Check if user can send group messages (dispatch, admin, super_admin)
  const canSendGroupMessage = ['admin', 'dispatch', 'super_admin'].includes(user?.role);

  // Fetch drivers for group messaging
  const fetchDrivers = useCallback(async () => {
    if (!canSendGroupMessage) return;
    try {
      const response = await api.get('/messages/drivers');
      setDrivers(response.data);
    } catch (error) {
      console.error('Failed to fetch drivers:', error);
    }
  }, [api, canSendGroupMessage]);

  // Edit message handler
  const handleEditMessage = async (messageId) => {
    if (!editContent.trim()) return;
    try {
      await api.put(`/messages/${messageId}`, { content: editContent.trim() });
      setMessages(prev => prev.map(m => 
        m.id === messageId ? { ...m, content: editContent.trim(), edited: true, edited_at: new Date().toISOString() } : m
      ));
      setEditingMessageId(null);
      setEditContent('');
      toast.success('Message updated');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to edit message');
    }
  };

  const canEditMessage = (msg) => {
    if (msg.sender_id !== user?.user_id) return false;
    if (msg.message_type === 'image' || msg.message_type === 'file') return false;
    if (msg.deleted) return false;
    const created = new Date(msg.created_at);
    const elapsed = (Date.now() - created.getTime()) / 1000;
    return elapsed <= 3600; // 1 hour
  };

  const canDeleteMessage = (msg) => {
    if (msg.sender_id !== user?.user_id) return false;
    if (msg.deleted) return false;
    const created = new Date(msg.created_at);
    return (Date.now() - created.getTime()) / 1000 <= 3600;
  };

  const handleDeleteMessage = async (messageId) => {
    try {
      await api.delete(`/messages/${messageId}`);
      setMessages(prev => prev.map(m =>
        m.id === messageId ? { ...m, content: 'This message was deleted', deleted: true, attachments: [], file_url: null } : m
      ));
      toast.success('Message deleted');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete message');
    }
  };



  // Send group message to all drivers
  const handleSendGroupMessage = async () => {
    if (!groupMessage.trim()) {
      toast.error('Please enter a message');
      return;
    }
    
    setSendingGroupMessage(true);
    try {
      const response = await api.post('/messages/group/drivers', {
        receiver_id: 'all_drivers',
        content: groupMessage
      });
      
      toast.success(response.data.message || `Message sent to ${response.data.sent_to} drivers`);
      setGroupMessage('');
      setShowGroupMessage(false);
      fetchConversations(); // Refresh conversations list
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send group message');
    } finally {
      setSendingGroupMessage(false);
    }
  };

  // Fetch driver's current task/dispatch
  const fetchDriverTasks = useCallback(async () => {
    try {
      const [dispatchesRes, driversRes] = await Promise.all([
        api.get('/dispatches'),
        api.get('/drivers')
      ]);
      
      const dispatches = dispatchesRes.data;
      const drivers = driversRes.data;
      
      // Create a map of driver user_id to their active task
      const taskMap = {};
      
      for (const driver of drivers) {
        // Find active dispatch for this driver
        const activeDispatch = dispatches.find(d => 
          d.driver_id === driver.id && 
          ['WAITING FOR CONFIRMATION', 'ACCEPTED', 'IN PROGRESS', 'PENDING'].includes(d.status)
        );
        
        if (activeDispatch && driver.user_id) {
          // Build summary from task_type
          const taskLabels = {
            'LOAD_OUT_DELIVER': 'Loading out',
            'EMPTY_OUT_DELIVER': 'Empty out',
            'RETURN_EMPTY': 'Returning empty',
            'RETURN_LOAD': 'Returning load',
            'DELIVER_FROM_TO': 'Delivering'
          };
          
          let summary = '';
          if (activeDispatch.task_type) {
            summary = taskLabels[activeDispatch.task_type] || activeDispatch.task_type;
            if (activeDispatch.container_number) {
              summary += ` ${activeDispatch.container_number}`;
            }
            if (activeDispatch.destination) {
              summary += ` → ${activeDispatch.destination}`;
            }
          } else if (activeDispatch.task) {
            summary = activeDispatch.task;
          } else if (activeDispatch.container_number) {
            summary = `Working on ${activeDispatch.container_number}`;
            if (activeDispatch.destination) {
              summary += ` → ${activeDispatch.destination}`;
            }
          }
          
          taskMap[driver.user_id] = {
            summary: summary || 'Active dispatch',
            status: activeDispatch.status,
            container: activeDispatch.container_number,
            dispatch_id: activeDispatch.id
          };
        }
      }
      
      setDriverTasks(taskMap);
    } catch (error) {
      console.error('Failed to fetch driver tasks:', error);
    }
  }, [api]);

  const fetchConversations = useCallback(async () => {
    try {
      const response = await api.get('/messages/conversations');
      setConversations(response.data);
    } catch (error) {
      console.error('Failed to fetch conversations');
    } finally {
      setLoading(false);
    }
  }, [api]);

  // Keep ref updated
  useEffect(() => {
    fetchConversationsRef.current = fetchConversations;
  }, [fetchConversations]);

  const fetchUsers = useCallback(async () => {
    try {
      const response = await api.get('/users');
      setUsers(response.data.filter(u => u.user_id !== user?.user_id));
    } catch (error) {
      console.error('Failed to fetch users');
    }
  }, [api, user]);

  const fetchMessages = useCallback(async (otherUserId) => {
    setMessageLoading(true);
    try {
      const response = await api.get(`/messages/conversation/${otherUserId}`);
      setMessages(response.data);
    } catch (error) {
      console.error('Failed to fetch messages:', error);
      // Don't show toast for empty conversations - this is expected for new chats
      if (error.response?.status !== 404) {
        toast.error('Failed to fetch messages');
      }
    } finally {
      setMessageLoading(false);
    }
  }, [api]);

  useEffect(() => {
    fetchConversations();
    fetchUsers();
    fetchDriverTasks();
    fetchDrivers();
    
    // Refresh driver tasks every 30 seconds
    const interval = setInterval(fetchDriverTasks, 30000);
    return () => clearInterval(interval);
  }, [fetchConversations, fetchUsers, fetchDriverTasks, fetchDrivers]);

  const isInitialLoadRef = useRef(true);

  useEffect(() => {
    if (selectedConversation?.other_user?.user_id) {
      isInitialLoadRef.current = true;
      fetchMessages(selectedConversation.other_user.user_id);
    }
  }, [selectedConversation, fetchMessages]);

  // Scroll to bottom - uses requestAnimationFrame with retries for reliability
  const scrollToBottom = useCallback((instant = false) => {
    let attempts = 0;
    const maxAttempts = 25;
    
    const doScroll = () => {
      attempts++;
      const el = messagesEndRef.current;
      if (el) {
        const overflowParent = el.closest('.overflow-y-auto') || el.closest('[data-radix-scroll-area-viewport]') || el.parentElement;
        if (overflowParent) {
          overflowParent.scrollTop = overflowParent.scrollHeight;
        }
      }
      // Keep retrying even if ref isn't mounted yet
      if (attempts < maxAttempts) {
        requestAnimationFrame(doScroll);
      }
    };
    
    // Primary scroll after short delay
    const timer = setTimeout(() => {
      requestAnimationFrame(doScroll);
    }, 200);
    
    // Secondary scroll for images/heavy content
    const timer2 = setTimeout(() => {
      const el = messagesEndRef.current;
      if (el) {
        const overflowParent = el.closest('.overflow-y-auto') || el.closest('[data-radix-scroll-area-viewport]') || el.parentElement;
        if (overflowParent) {
          overflowParent.scrollTop = overflowParent.scrollHeight;
        }
      }
    }, 800);
    
    return () => { clearTimeout(timer); clearTimeout(timer2); };
  }, []);

  useEffect(() => {
    if (!messages.length || messageLoading) return;
    const timer = scrollToBottom(isInitialLoadRef.current);
    isInitialLoadRef.current = false;
    return () => clearTimeout(timer);
  }, [messages, messageLoading, scrollToBottom]);

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if ((!newMessage.trim() && pendingAttachments.length === 0) || !selectedConversation) return;

    // Clear typing indicator and debounce
    if (typingDebounceRef.current) {
      clearTimeout(typingDebounceRef.current);
    }
    isTypingRef.current = false;
    sendTypingIndicator(false);
    
    try {
      const receiverId = selectedConversation.other_user?.user_id;
      
      if (pendingAttachments.length > 0) {
        // Send each attachment as a separate message
        for (let i = 0; i < pendingAttachments.length; i++) {
          const att = pendingAttachments[i];
          const caption = i === 0 ? (newMessage.trim() || `📎 ${att.filename}`) : `📎 ${att.filename}`;
          await api.post('/messages/send', {
            receiver_id: receiverId,
            content: caption,
            message_type: att.is_image ? 'image' : 'file',
            file_url: att.url || null,
            attachments: [{
              file_id: att.file_id,
              filename: att.filename,
              is_image: att.is_image,
              content_type: att.content_type
            }]
          });
        }
      } else {
        await api.post('/messages/send', {
          receiver_id: receiverId,
          content: newMessage.trim(),
          message_type: 'text',
          file_url: null
        });
      }
      
      setNewMessage('');
      setPendingAttachments([]);
      fetchMessages(selectedConversation.other_user.user_id);
      fetchConversations();
    } catch (error) {
      toast.error('Failed to send message');
    }
  };

  const handleStartNewChat = async (otherUser) => {
    // Create a pseudo conversation
    const userIds = [user.user_id, otherUser.user_id].sort();
    const conversationId = `${userIds[0]}_${userIds[1]}`;
    
    setSelectedConversation({
      conversation_id: conversationId,
      other_user: otherUser
    });
    setShowNewChat(false);
    setMessages([]);
  };

  const handleFileUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;

    // Validate all files
    const oversized = files.filter(f => f.size > 5 * 1024 * 1024);
    if (oversized.length > 0) {
      toast.error(`${oversized.length} file(s) exceed 5MB limit`);
      return;
    }

    setUploadingFile(true);
    const uploaded = [];
    const failed = [];

    for (const file of files) {
      try {
        const formData = new FormData();
        formData.append('file', file);
        const response = await api.post('/messages/upload-attachment', formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        
        uploaded.push({
          file_id: response.data.file_id,
          filename: response.data.filename,
          is_image: file.type.startsWith('image/'),
          content_type: file.type
        });

        if (response.data.auto_import?.auto_imported) {
          const docType = response.data.auto_import.document_type || 'Document';
          toast.success(`${docType} auto-imported to ${response.data.auto_import.shipment_job_number}`, { duration: 5000 });
        }
      } catch {
        failed.push(file.name);
      }
    }

    if (uploaded.length > 0) {
      setPendingAttachments(prev => [...prev, ...uploaded]);
      toast.success(`${uploaded.length} file(s) ready to send`);
    }
    if (failed.length > 0) {
      toast.error(`Failed: ${failed.join(', ')}`);
    }

    setUploadingFile(false);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const removePendingAttachment = (index) => {
    setPendingAttachments(prev => prev.filter((_, i) => i !== index));
  };

  const getFileUrl = (fileId) => {
    return `${API_URL}/api/files/${fileId}`;
  };

  const formatTime = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
  };

  const filteredUsers = users.filter(u =>
    u.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    u.email?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Highlight matching text in search results
  const highlightMatch = (text, search) => {
    if (!search?.trim() || !text) return text;
    
    const searchLower = search.toLowerCase();
    const textLower = text.toLowerCase();
    const index = textLower.indexOf(searchLower);
    
    if (index === -1) return text;
    
    const before = text.slice(0, index);
    const match = text.slice(index, index + search.length);
    const after = text.slice(index + search.length);
    
    return (
      <>
        {before}
        <mark className="bg-amber-500/40 text-amber-200 px-0.5 rounded">{match}</mark>
        {after}
      </>
    );
  };

  // Search messages on backend when user types in search box
  useEffect(() => {
    if (!conversationSearchTerm.trim() || conversationSearchTerm.trim().length < 2) {
      setSearchMatchingUserIds(null);
      return;
    }
    const timer = setTimeout(async () => {
      try {
        const response = await api.get(`/messages/search?q=${encodeURIComponent(conversationSearchTerm.trim())}`);
        setSearchMatchingUserIds(response.data.matching_user_ids || []);
      } catch {
        setSearchMatchingUserIds([]);
      }
    }, 400);
    return () => clearTimeout(timer);
  }, [conversationSearchTerm, api]);

  // Filter conversations by search term (user name, container number, dispatch info, message content)
  const filteredConversations = conversations.filter(conv => {
    if (!conversationSearchTerm.trim()) return true;
    
    const searchLower = conversationSearchTerm.toLowerCase().trim();
    const otherUser = conv.other_user;
    
    // Search by user name
    if (otherUser?.name?.toLowerCase().includes(searchLower)) return true;
    
    // Search by user email
    if (otherUser?.email?.toLowerCase().includes(searchLower)) return true;
    
    // Search by user role (e.g., "driver", "dispatch")
    if (otherUser?.role?.toLowerCase().includes(searchLower)) return true;
    
    // Search by driver's current task/dispatch info
    const driverTask = driverTasks[otherUser?.user_id];
    if (driverTask) {
      // Search in task summary (includes container number, destination)
      if (driverTask.summary?.toLowerCase().includes(searchLower)) return true;
      // Search by container number directly
      if (driverTask.container?.toLowerCase().includes(searchLower)) return true;
    }
    
    // Search by last message content
    if (conv.last_message?.content?.toLowerCase().includes(searchLower)) return true;
    
    // Match by backend message content search results
    if (searchMatchingUserIds && searchMatchingUserIds.includes(otherUser?.user_id)) return true;
    
    return false;
  });

  return (
    <div className="flex h-[calc(100vh-64px)]">
      {/* Conversations List */}
      <div className={`w-full md:w-80 bg-slate-900 border-r border-slate-800 flex flex-col ${
        selectedConversation ? 'hidden md:flex' : 'flex'
      }`}>
        <div className="p-4 border-b border-slate-800">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-heading font-semibold text-white">Messages</h2>
            <div className="flex items-center gap-2">
              {canSendGroupMessage && (
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => setShowGroupMessage(true)}
                  className="border-green-600 text-green-400 hover:bg-green-600 hover:text-white"
                  data-testid="group-message-btn"
                >
                  <Truck className="h-4 w-4 mr-1" />
                  All Drivers
                </Button>
              )}
              <Button
                size="sm"
                onClick={() => setShowNewChat(true)}
                className="bg-blue-600 hover:bg-blue-500"
                data-testid="new-chat-btn"
              >
                New Chat
              </Button>
            </div>
          </div>
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-500" />
            <Input
              placeholder="Search by name, container #, dispatch..."
              value={conversationSearchTerm}
              onChange={(e) => setConversationSearchTerm(e.target.value)}
              className="pl-10 bg-slate-950 border-slate-800 text-white"
              data-testid="conversation-search-input"
            />
            {conversationSearchTerm && (
              <button
                onClick={() => setConversationSearchTerm('')}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-white"
              >
                <X className="h-4 w-4" />
              </button>
            )}
          </div>
          {conversationSearchTerm && (
            <p className="text-xs text-slate-500 mt-2">
              {filteredConversations.length} of {conversations.length} conversations
            </p>
          )}
        </div>

        <ScrollArea className="flex-1">
          {showNewChat ? (
            <div className="p-4">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setShowNewChat(false)}
                className="mb-4 text-slate-400"
              >
                <ArrowLeft className="h-4 w-4 mr-2" />
                Back
              </Button>
              <Input
                placeholder="Search users..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="bg-slate-950 border-slate-800 text-white mb-4"
              />
              {filteredUsers.map(u => (
                <button
                  key={u.user_id}
                  onClick={() => handleStartNewChat(u)}
                  className="w-full flex items-center gap-3 p-3 rounded-sm hover:bg-slate-800 transition-colors"
                >
                  <Avatar className="h-10 w-10">
                    <AvatarImage src={u.picture} />
                    <AvatarFallback className="bg-blue-600 text-white">
                      {u.name?.charAt(0).toUpperCase()}
                    </AvatarFallback>
                  </Avatar>
                  <div className="text-left">
                    <p className="text-white font-medium">{u.name}</p>
                    <p className="text-slate-400 text-xs">{u.role}</p>
                  </div>
                </button>
              ))}
            </div>
          ) : loading ? (
            <div className="flex items-center justify-center py-20">
              <Loader2 className="h-6 w-6 animate-spin text-blue-500" />
            </div>
          ) : filteredConversations.length === 0 ? (
            <div className="text-center py-20 text-slate-500">
              {conversationSearchTerm ? (
                <div>
                  <p>No conversations found for "{conversationSearchTerm}"</p>
                  <Button
                    variant="link"
                    onClick={() => setConversationSearchTerm('')}
                    className="text-blue-400 mt-2"
                  >
                    Clear search
                  </Button>
                </div>
              ) : (
                'No conversations yet'
              )}
            </div>
          ) : (
            filteredConversations.map(conv => (
              <button
                key={conv.conversation_id}
                data-testid={`conversation-item-${conv.other_user?.user_id}`}
                onClick={() => setSelectedConversation(conv)}
                className={`w-full flex items-start gap-3 p-4 hover:bg-slate-800 transition-colors border-b border-slate-800 ${
                  selectedConversation?.conversation_id === conv.conversation_id ? 'bg-slate-800' : 
                  conv.unread_count > 0 ? 'bg-blue-900/20 border-l-2 border-l-blue-400' : ''
                }`}
              >
                <div className="relative">
                  <Avatar className="h-12 w-12 flex-shrink-0">
                    <AvatarImage src={conv.other_user?.picture} />
                    <AvatarFallback className={conv.unread_count > 0 ? 'bg-blue-500 text-white' : 'bg-blue-600 text-white'}>
                      {conv.other_user?.name?.charAt(0).toUpperCase()}
                    </AvatarFallback>
                  </Avatar>
                  {conv.unread_count > 0 && (
                    <span className="absolute -top-1 -right-1 w-3 h-3 bg-green-400 rounded-full border-2 border-slate-900 animate-pulse" />
                  )}
                </div>
                <div className="flex-1 text-left min-w-0">
                  <div className="flex items-center justify-between">
                    <p className={`font-medium truncate ${conv.unread_count > 0 ? 'text-green-400 font-bold' : 'text-white'}`}>
                      {conversationSearchTerm ? highlightMatch(conv.other_user?.name, conversationSearchTerm) : conv.other_user?.name}
                    </p>
                    {conv.unread_count > 0 && (
                      <span className="bg-red-500 text-white text-xs px-2 py-0.5 rounded-full font-bold" data-testid={`unread-badge-${conv.conversation_id}`}>
                        {conv.unread_count}
                      </span>
                    )}
                  </div>
                  
                  {/* Driver task summary in conversation list */}
                  {conv.other_user?.role === 'driver' && driverTasks[conv.other_user?.user_id] ? (
                    <div className="flex items-center gap-1 mt-1">
                      <Truck className="h-3 w-3 text-blue-400 flex-shrink-0" />
                      <p className="text-blue-400 text-xs truncate">
                        {conversationSearchTerm 
                          ? highlightMatch(driverTasks[conv.other_user.user_id].summary, conversationSearchTerm)
                          : driverTasks[conv.other_user.user_id].summary}
                      </p>
                    </div>
                  ) : typingUsers[conv.other_user?.user_id] ? (
                    <div className="flex items-center gap-1">
                      <span className="flex gap-0.5">
                        <span className="w-1.5 h-1.5 bg-green-400 rounded-full animate-bounce" style={{animationDelay: '0ms'}}></span>
                        <span className="w-1.5 h-1.5 bg-green-400 rounded-full animate-bounce" style={{animationDelay: '150ms'}}></span>
                        <span className="w-1.5 h-1.5 bg-green-400 rounded-full animate-bounce" style={{animationDelay: '300ms'}}></span>
                      </span>
                      <span className="text-green-400 text-sm italic">typing...</span>
                    </div>
                  ) : (
                    <p className="text-slate-400 text-sm truncate">
                      {conversationSearchTerm 
                        ? highlightMatch(conv.last_message?.content, conversationSearchTerm)
                        : conv.last_message?.content}
                    </p>
                  )}
                </div>
              </button>
            ))
          )}
        </ScrollArea>
      </div>

      {/* Chat Area */}
      <div className={`flex-1 flex flex-col bg-slate-950 ${
        !selectedConversation ? 'hidden md:flex' : 'flex'
      }`}>
        {selectedConversation ? (
          <>
            {/* Chat Header */}
            <div className="p-4 border-b border-slate-800 bg-slate-900">
              <div className="flex items-center gap-3">
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => {
                    setSelectedConversation(null);
                    // Refresh conversations to update read status
                    fetchConversations();
                  }}
                  className="md:hidden text-slate-400"
                >
                  <ArrowLeft className="h-5 w-5" />
                </Button>
                <Avatar className="h-10 w-10">
                  <AvatarImage src={selectedConversation.other_user?.picture} />
                  <AvatarFallback className="bg-blue-600 text-white">
                    {selectedConversation.other_user?.name?.charAt(0).toUpperCase()}
                  </AvatarFallback>
                </Avatar>
                <div className="flex-1">
                  <p className="text-white font-medium">{selectedConversation.other_user?.name}</p>
                  <p className="text-slate-400 text-xs">{selectedConversation.other_user?.role}</p>
                </div>
              </div>
              
              {/* Driver Task Summary */}
              {driverTasks[selectedConversation.other_user?.user_id] && (
                <div className="mt-3 p-2 bg-blue-500/10 border border-blue-500/30 rounded-lg" data-testid="driver-task-summary">
                  <div className="flex items-center gap-2">
                    <Truck className="h-4 w-4 text-blue-400" />
                    <span className="text-blue-400 text-xs font-medium">CURRENT TASK</span>
                    <Badge 
                      variant="outline" 
                      className={`text-xs ml-auto ${
                        driverTasks[selectedConversation.other_user.user_id].status === 'IN PROGRESS' 
                          ? 'border-green-500 text-green-400' 
                          : driverTasks[selectedConversation.other_user.user_id].status === 'ACCEPTED'
                          ? 'border-blue-500 text-blue-400'
                          : 'border-amber-500 text-amber-400'
                      }`}
                    >
                      {driverTasks[selectedConversation.other_user.user_id].status}
                    </Badge>
                  </div>
                  <p className="text-white text-sm mt-1 font-medium">
                    {driverTasks[selectedConversation.other_user.user_id].summary}
                  </p>
                </div>
              )}
            </div>

            {/* Messages */}
            <ScrollArea className="flex-1 p-4">
              {messageLoading ? (
                <div className="flex items-center justify-center py-20">
                  <Loader2 className="h-6 w-6 animate-spin text-blue-500" />
                </div>
              ) : messages.length === 0 ? (
                <div className="text-center py-20 text-slate-500">
                  No messages yet. Start the conversation!
                </div>
              ) : (
                <div className="space-y-4">
                  {messages.map(msg => (
                    <div
                      key={msg.id}
                      className={`flex ${msg.sender_id === user?.user_id ? 'justify-end' : 'justify-start'} group`}
                    >
                      <div className={`flex items-end gap-1 ${msg.sender_id === user?.user_id ? 'flex-row-reverse' : 'flex-row'}`}>
                        {/* Message bubble */}
                        <div
                          className={`max-w-[70%] rounded-lg px-4 py-2 ${
                            msg.sender_id === user?.user_id
                              ? 'bg-blue-600 text-white'
                              : 'bg-slate-800 text-white'
                          }`}
                        >
                        {/* Render attachments/images */}
                        {msg.attachments && msg.attachments.length > 0 && (
                          <div className="mb-2">
                            {msg.attachments.map((att, idx) => (
                              <div key={idx} className="mb-2">
                                {att.is_image ? (
                                  <img
                                    src={getFileUrl(att.file_id)}
                                    alt={att.filename}
                                    className="max-w-full rounded-lg cursor-pointer hover:opacity-90 transition-opacity"
                                    style={{ maxHeight: '200px' }}
                                    onClick={() => setPreviewImage(getFileUrl(att.file_id))}
                                    data-testid="message-image"
                                  />
                                ) : (
                                  <a
                                    href={getFileUrl(att.file_id)}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="flex items-center gap-2 text-sm underline"
                                  >
                                    <Paperclip className="h-4 w-4" />
                                    {att.filename}
                                  </a>
                                )}
                              </div>
                            ))}
                          </div>
                        )}
                        {/* Fallback: Render image from file_url if no attachments (legacy messages) */}
                        {(!msg.attachments || msg.attachments.length === 0) && msg.message_type === 'image' && msg.file_url && (
                          <div className="mb-2">
                            <img
                              src={getFileUrl(msg.file_url)}
                              alt="Photo"
                              className="max-w-full rounded-lg cursor-pointer hover:opacity-90 transition-opacity"
                              style={{ maxHeight: '200px' }}
                              onClick={() => setPreviewImage(getFileUrl(msg.file_url))}
                              data-testid="message-image-legacy"
                            />
                          </div>
                        )}
                        {msg.content && msg.content !== '📷 Photo' && (
                          editingMessageId === msg.id ? (
                            <div className="flex gap-1 items-center">
                              <input
                                type="text"
                                value={editContent}
                                onChange={(e) => setEditContent(e.target.value)}
                                onKeyDown={(e) => {
                                  if (e.key === 'Enter') handleEditMessage(msg.id);
                                  if (e.key === 'Escape') { setEditingMessageId(null); setEditContent(''); }
                                }}
                                className="flex-1 bg-black/20 text-white text-sm rounded px-2 py-1 border border-white/20 outline-none"
                                autoFocus
                                data-testid="edit-message-input"
                              />
                              <button onClick={() => handleEditMessage(msg.id)} className="text-green-300 hover:text-green-200 p-0.5" data-testid="save-edit-btn">
                                <Check className="h-4 w-4" />
                              </button>
                              <button onClick={() => { setEditingMessageId(null); setEditContent(''); }} className="text-red-300 hover:text-red-200 p-0.5">
                                <X className="h-3.5 w-3.5" />
                              </button>
                            </div>
                          ) : (
                            <div className="group/msg relative">
                              <p>{msg.content}</p>
                              {msg.moving_job_id && (
                                <Link to={`/moving?comments=${msg.moving_job_id}`} data-testid={`msg-open-job-${msg.id}`}
                                  className="mt-1.5 inline-flex items-center gap-1 rounded-md bg-sky-600 px-2 py-1 text-xs font-semibold text-white hover:bg-sky-500">
                                  <MessageSquare className="h-3.5 w-3.5" />Open job comments
                                </Link>
                              )}
                              {msg.edited && !msg.deleted && <span className="text-[10px] opacity-50 italic">edited</span>}
                              {!msg.deleted && (canEditMessage(msg) || canDeleteMessage(msg)) && (
                                <div className="absolute -top-2 -right-2 opacity-0 group-hover/msg:opacity-100 transition-opacity flex gap-0.5">
                                  {canEditMessage(msg) && (
                                    <button onClick={() => { setEditingMessageId(msg.id); setEditContent(msg.content); }}
                                      className="bg-slate-700 rounded-full p-1 shadow" title="Edit" data-testid={`edit-msg-${msg.id}`}>
                                      <Pencil className="h-3 w-3 text-slate-300" />
                                    </button>
                                  )}
                                  {canDeleteMessage(msg) && (
                                    <button onClick={() => handleDeleteMessage(msg.id)}
                                      className="bg-slate-700 rounded-full p-1 shadow hover:bg-red-700" title="Delete" data-testid={`delete-msg-${msg.id}`}>
                                      <Trash2 className="h-3 w-3 text-slate-300" />
                                    </button>
                                  )}
                                </div>
                              )}
                            </div>
                          )
                        )}
                        <div className={`flex items-center justify-end gap-1 mt-1 ${
                          msg.sender_id === user?.user_id ? 'text-blue-200' : 'text-slate-400'
                        }`}>
                          <span className="text-xs">{formatTime(msg.created_at)}</span>
                          <MessageStatus message={msg} isOwnMessage={msg.sender_id === user?.user_id} />
                        </div>
                        
                        {/* Reactions display */}
                        <MessageReactions 
                          reactions={msg.reactions} 
                          currentUserId={user?.user_id}
                          onReact={(reaction) => handleReaction(msg.id, reaction)}
                        />
                      </div>
                      
                      {/* Reaction picker - shows on hover */}
                      <ReactionPicker 
                        onReact={(reaction) => handleReaction(msg.id, reaction)}
                        existingReactions={msg.reactions}
                        currentUserId={user?.user_id}
                      />
                      </div>
                    </div>
                  ))}
                  
                  {/* Typing Indicator */}
                  {otherUserTyping && (
                    <div data-testid="typing-indicator-bubble" className="flex justify-start">
                      <div className="max-w-[70%] rounded-lg px-4 py-2 bg-slate-800 text-white">
                        <div className="flex items-center gap-2">
                          <div className="flex gap-1">
                            <span className="w-2 h-2 bg-blue-400 rounded-full animate-bounce" style={{animationDelay: '0ms'}}></span>
                            <span className="w-2 h-2 bg-blue-400 rounded-full animate-bounce" style={{animationDelay: '150ms'}}></span>
                            <span className="w-2 h-2 bg-blue-400 rounded-full animate-bounce" style={{animationDelay: '300ms'}}></span>
                          </div>
                          <span className="text-xs text-slate-400">{selectedConversation?.other_user?.name} is typing...</span>
                        </div>
                      </div>
                    </div>
                  )}
                  
                  <div ref={messagesEndRef} />
                </div>
              )}
            </ScrollArea>

            {/* Message Input */}
            <form onSubmit={handleSendMessage} className="p-4 border-t border-slate-800 bg-slate-900">
              {/* Pending Attachments Preview */}
              {pendingAttachments.length > 0 && (
                <div className="mb-3 p-2 bg-slate-800 rounded-lg space-y-1">
                  {pendingAttachments.map((att, idx) => (
                    <div key={idx} className="flex items-center justify-between">
                      <div className="flex items-center gap-2 text-sm text-slate-300">
                        {att.is_image ? <Image className="h-4 w-4 text-blue-400" /> : <Paperclip className="h-4 w-4 text-orange-400" />}
                        <span className="truncate max-w-[200px]">{att.filename}</span>
                      </div>
                      <Button type="button" variant="ghost" size="icon" onClick={() => removePendingAttachment(idx)} className="h-6 w-6 text-slate-400 hover:text-white">
                        <X className="h-4 w-4" />
                      </Button>
                    </div>
                  ))}
                </div>
              )}
              
              <div className="flex items-center gap-2">
                <input
                  type="file"
                  ref={fileInputRef}
                  onChange={handleFileUpload}
                  className="hidden"
                  accept="image/*,.pdf,.doc,.docx"
                  multiple
                  data-testid="file-input"
                />
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  onClick={() => fileInputRef.current?.click()}
                  className="text-slate-400 hover:text-white"
                  disabled={uploadingFile}
                  data-testid="attach-photo-btn"
                >
                  {uploadingFile ? (
                    <Loader2 className="h-5 w-5 animate-spin" />
                  ) : (
                    <Image className="h-5 w-5" />
                  )}
                </Button>
                <Input
                  value={newMessage}
                  onChange={handleMessageInputChange}
                  onBlur={() => sendTypingIndicator(false)}
                  placeholder={pendingAttachments.length > 0 ? "Add a caption..." : "Type a message..."}
                  className="flex-1 bg-slate-950 border-slate-800 text-white"
                  data-testid="message-input"
                />
                <Button
                  type="submit"
                  disabled={!newMessage.trim() && pendingAttachments.length === 0}
                  className="bg-blue-600 hover:bg-blue-500"
                  data-testid="send-message-btn"
                >
                  <Send className="h-4 w-4" />
                </Button>
              </div>
            </form>
          </>
        ) : (
          <div className="flex-1 flex items-center justify-center text-slate-500">
            Select a conversation or start a new chat
          </div>
        )}
      </div>

      {/* Image Preview Modal */}
      {previewImage && (
        <div 
          className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center p-4"
          onClick={() => setPreviewImage(null)}
        >
          <Button
            variant="ghost"
            size="icon"
            className="absolute top-4 right-4 text-white hover:bg-white/20"
            onClick={() => setPreviewImage(null)}
          >
            <X className="h-6 w-6" />
          </Button>
          <img
            src={previewImage}
            alt="Preview"
            className="max-w-full max-h-[90vh] object-contain rounded-lg"
            onClick={(e) => e.stopPropagation()}
          />
          <a
            href={previewImage}
            download
            className="absolute bottom-4 right-4"
            onClick={(e) => e.stopPropagation()}
          >
            <Button variant="secondary" className="bg-white/20 hover:bg-white/30 text-white">
              <Download className="h-4 w-4 mr-2" />
              Download
            </Button>
          </a>
        </div>
      )}

      {/* Group Message Modal */}
      {showGroupMessage && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
          <Card className="bg-slate-900 border-slate-700 w-full max-w-lg p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-white flex items-center gap-2">
                <Truck className="h-5 w-5 text-green-400" />
                Message All Drivers
              </h3>
              <Button
                variant="ghost"
                size="icon"
                onClick={() => setShowGroupMessage(false)}
                className="text-slate-400 hover:text-white"
              >
                <X className="h-5 w-5" />
              </Button>
            </div>
            
            <p className="text-slate-400 text-sm mb-4">
              Send a message to all {drivers.length} drivers in your company at once.
            </p>
            
            {drivers.length > 0 && (
              <div className="mb-4 p-3 bg-slate-800 rounded-lg max-h-32 overflow-y-auto">
                <p className="text-xs text-slate-500 mb-2">Recipients:</p>
                <div className="flex flex-wrap gap-2">
                  {drivers.map(driver => (
                    <Badge key={driver.user_id} variant="secondary" className="bg-slate-700 text-slate-300">
                      {driver.name}
                    </Badge>
                  ))}
                </div>
              </div>
            )}
            
            <textarea
              value={groupMessage}
              onChange={(e) => setGroupMessage(e.target.value)}
              placeholder="Type your message to all drivers..."
              className="w-full h-32 px-3 py-2 bg-slate-950 border border-slate-700 rounded-lg text-white placeholder:text-slate-500 resize-none focus:outline-none focus:ring-2 focus:ring-green-500 mb-4"
              data-testid="group-message-input"
            />
            
            <div className="flex justify-end gap-3">
              <Button
                variant="outline"
                onClick={() => {
                  setShowGroupMessage(false);
                  setGroupMessage('');
                }}
                className="border-slate-700 text-slate-300"
              >
                Cancel
              </Button>
              <Button
                onClick={handleSendGroupMessage}
                disabled={!groupMessage.trim() || sendingGroupMessage || drivers.length === 0}
                className="bg-green-600 hover:bg-green-500"
                data-testid="send-group-message-btn"
              >
                {sendingGroupMessage ? (
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                ) : (
                  <Send className="h-4 w-4 mr-2" />
                )}
                Send to {drivers.length} Drivers
              </Button>
            </div>
          </Card>
        </div>
      )}
    </div>
  );
};

export default Messaging;
